/**
 * Development tools for Supernal TTS Widget
 */

import { CacheManager } from '../core/cache';

export class DevTools {
  private cacheManager: CacheManager;
  private devMode: boolean;

  constructor(cacheManager: CacheManager, devMode: boolean) {
    this.cacheManager = cacheManager;
    this.devMode = devMode;
  }

  /**
   * Dev Mode Cache Clear Button
   * Adds a minimizable floating button in dev mode to clear local cache
   * Only appears on pages that have TTS widgets
   */
  public addClearCacheButton(): void {
    if (!this.devMode) return;

    // Only add once
    if (document.getElementById('supernal-tts-dev-clear-cache')) {
      return;
    }

    // Create container
    const container = document.createElement('div');
    container.id = 'supernal-tts-dev-clear-cache';
    container.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 100;
      font-family: system-ui, -apple-system, sans-serif;
    `;

    // Minimized button (trash icon only)
    const minimizedBtn = document.createElement('button');
    minimizedBtn.className = 'supernal-tts-dev-button-minimized';
    minimizedBtn.innerHTML = '🗑️';
    minimizedBtn.title = 'Clear TTS Cache (Dev Mode)';
    minimizedBtn.style.cssText = `
      display: none;
      background: #6c757d;
      color: white;
      border: none;
      padding: 8px;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      font-size: 16px;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      transition: all 0.2s ease;
    `;

    // Full button
    const fullBtn = document.createElement('button');
    fullBtn.className = 'supernal-tts-dev-button';
    fullBtn.style.cssText = `
      background: #dc3545;
      color: white;
      border: none;
      padding: 8px 12px;
      border-radius: 5px;
      font-size: 12px;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 6px;
    `;
    fullBtn.innerHTML = `
      <span>🗑️</span>
      <span>Clear Cache</span>
      <span style="opacity: 0.7; font-size: 10px;">×</span>
    `;

    // Hover effects
    const addHoverEffect = (btn: HTMLButtonElement) => {
      btn.addEventListener('mouseenter', () => {
        btn.style.transform = 'scale(1.05)';
        btn.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transform = 'scale(1)';
        btn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
      });
    };

    addHoverEffect(fullBtn);
    addHoverEffect(minimizedBtn);

    // Clear cache handler
    const clearCache = () => {
      const count = this.cacheManager.size;
      this.cacheManager.clear();

      // Visual feedback
      fullBtn.innerHTML = `<span>✓</span><span>Cleared ${count}</span><span style="opacity: 0.7; font-size: 10px;">×</span>`;
      fullBtn.style.background = '#28a745';

      setTimeout(() => {
        fullBtn.innerHTML = `<span>🗑️</span><span>Clear Cache</span><span style="opacity: 0.7; font-size: 10px;">×</span>`;
        fullBtn.style.background = '#dc3545';
      }, 2000);

      console.log(`[Supernal TTS Dev] Cleared ${count} cached items`);
    };

    // Minimize handler
    fullBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const clickX = e.clientX - (e.target as HTMLElement).getBoundingClientRect().left;
      const btnWidth = fullBtn.getBoundingClientRect().width;

      // If clicked on the × (right 20% of button), minimize
      if (clickX > btnWidth * 0.8) {
        fullBtn.style.display = 'none';
        minimizedBtn.style.display = 'block';
        localStorage.setItem('supernal-tts-dev-minimized', 'true');
      } else {
        clearCache();
      }
    });

    // Expand handler
    minimizedBtn.addEventListener('click', (e) => {
      e.preventDefault();
      minimizedBtn.style.display = 'none';
      fullBtn.style.display = 'flex';
      localStorage.setItem('supernal-tts-dev-minimized', 'false');
    });

    // Restore minimized state
    const isMinimized = localStorage.getItem('supernal-tts-dev-minimized') === 'true';
    if (isMinimized) {
      fullBtn.style.display = 'none';
      minimizedBtn.style.display = 'block';
    }

    container.appendChild(fullBtn);
    container.appendChild(minimizedBtn);

    // Add to DOM when ready
    if (document.body) {
      document.body.appendChild(container);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.appendChild(container);
      });
    }
  }
}
